import React, { FC, memo, useEffect, ReactNode } from 'react'
import { FeedbackMessage } from 'components/marketplace/FeedbackMessage'
import { Card } from 'transfix-ui/components/Card'
import { Spinner } from 'transfix-ui/components/Spinner'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { ConnectedPhoneNumber } from 'apps/marketplace/components/ConnectedPhoneNumber'
import { getSourceValue } from 'apps/user/components/utils'
import { TUserSources } from 'apps/user/types/user'
import { capitalize } from 'utils/capitalize'
import { userGroupBooleans } from 'utils/userTypes'
import { DISPLAY } from 'constants/display'
import { USER_TYPE } from 'constants/userTypes'

interface LabelValueProps {
  label: string
  value: string | ReactNode
}

export const LabelValue: FC<LabelValueProps> = memo(({ label, value }) => (
  <p>
    <span className='mr1 text-secondary'>{label}:</span>
    {value || DISPLAY.empty}
  </p>
))

interface IUserDetailsProps {
  applicationId: string
  firstName: string
  lastName: string
  phone: string
  phoneExt: string
  cellPhone: string
  email: string
  userType: USER_TYPE
  userTypeName: string
  role: string
  stateName: string
  companyId: number
  companyName: string
  companyLegalName: string
  companyType: string
  companyDot: string
  companyMc: string
  companyVisible: boolean
  showUserAccounts: boolean
  source: string
  edit: () => void
  getCarrierDetails: (companyId: number) => void
  carrierFetching: boolean
  carrierFetchError: string
}

export const UserDetailsComponent: FC<IUserDetailsProps> = memo(
  ({
    applicationId,
    carrierFetchError,
    carrierFetching,
    cellPhone,
    companyDot,
    companyId,
    companyLegalName,
    companyMc,
    companyName,
    companyType,
    edit,
    email,
    firstName,

    getCarrierDetails,
    lastName,
    phone,
    phoneExt,
    role,
    showUserAccounts,
    source,
    stateName,
    userType,
    userTypeName,
  }) => {
    const { userPermissions } = useUserDetails()
    const { navCarriers, navShippers, usersUpdate: showEdit } = userPermissions || {}
    const companyVisible =
      !['admin', 'bot'].includes(userType) && applicationId && navShippers && navCarriers
    const { isCarrier } = userGroupBooleans(userType)
    useEffect(() => {
      if (isCarrier && companyId) {
        getCarrierDetails(companyId)
      }
    }, [companyId])

    const linkUrl = `${__ROOT__}/${companyType}/${companyId}`

    return (
      <Card
        title='Details'
        callToAction={showEdit ? <button onClick={edit}>Edit</button> : undefined}>
        <>
          {[
            { label: 'First Name', value: firstName },
            { label: 'Last Name', value: lastName },
            {
              label: 'Phone',
              value: phone && (
                <ConnectedPhoneNumber phoneNumber={phone} phoneExtension={phoneExt} />
              ),
            },
            {
              label: 'Cell Phone',
              value: cellPhone && <ConnectedPhoneNumber phoneNumber={cellPhone} />,
            },
            { label: 'Email', value: email && <a href={`mailto:${email}`}>{email}</a> },
            ...(!source
              ? []
              : [
                  {
                    label: 'Created by',
                    value: getSourceValue(source as TUserSources),
                  },
                ]),
            ...(showUserAccounts
              ? []
              : [
                  { label: 'User Type', value: userTypeName || capitalize(userType) },
                  { label: 'Role', value: capitalize(role) },
                  { label: 'Status', value: stateName },
                ]),
          ].map(({ label, value }) => (
            <LabelValue key={label} label={label} value={value} />
          ))}
        </>
        {companyVisible && !showUserAccounts && (
          <>
            <strong>Company</strong>
            <p>
              <a href={linkUrl} target='_blank' rel='noopener noreferrer'>
                {companyName || companyLegalName}
              </a>
            </p>
            {isCarrier && (
              <>
                {carrierFetching && <Spinner position='relative' />}
                {carrierFetchError && <FeedbackMessage message={carrierFetchError} type='error' />}
                {carrierFetching || carrierFetchError
                  ? null
                  : [
                      { label: 'DOT', value: companyDot },
                      { label: 'MC', value: companyMc },
                    ].map(({ label, value }) => (
                      <LabelValue key={label} label={label} value={value} />
                    ))}
              </>
            )}
          </>
        )}
      </Card>
    )
  }
)
